import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown, ChevronUp, Award, GraduationCap, Heart } from 'lucide-react';
import { BOOKING_URL } from '@/config/site';

const ASSETS = {
  drJay: "/images/clinicians/jay.jpg",
  drGihani: "/images/clinicians/gihani.jpg",
  drRomeed: "/images/clinicians/shihab.jpg",
};

const clinicians = [
  {
    id: 'dr-romeed',
    name: 'Dr Shihab Romeed',
    role: 'Consultant in Restorative Dentistry & Implant Dentist',
    credentials: 'PhD, DDS, MSc | GDC: 103841',
    image: ASSETS.drRomeed,
    highlight: 'Clinical Director',
    shortBio: 'Dr Shihab Romeed is Clinical Director and Principal Implant Dentist at Specialist Dental London, with more than 25 years of experience in advanced restorative and implant dentistry.',
    fullBio: `Dr Shihab Romeed is the Clinical Director and Principal Implant Dentist at Specialist Dental London, with over 25 years of experience in advanced restorative and implant dentistry. As a Consultant in Restorative Dentistry, he leads a multidisciplinary team focused on dental implants, full-mouth rehabilitation, and complex restorative treatment.

He is a UK-trained consultant with specialist registration in Restorative Dentistry, Prosthodontics, Periodontics, and Endodontics with the General Dental Council. He completed a PhD in Restorative Dentistry at the University of Manchester, focused on prosthetic design and biomechanics, followed by higher specialist training at King's College London.

His postgraduate qualifications include MFDS RCS (Eng & Ed), FFD RCSI, FDS RCPS (Glasg), PGDip, and FHEA. His training pathway includes fellowships and diplomas from all four Royal Surgical Colleges in the UK and Ireland.

Clinically, Dr Romeed focuses on implant dentistry, from single-tooth replacement to full-arch reconstruction, including complex cases with bone loss or previous implant failure. His patient-first style combines precise planning with advanced techniques to restore both function and confidence.

Alongside clinical work, he is founder of London Dental Academy and has held senior academic roles at King's College London. He lectures internationally on implant rehabilitation and maxillofacial reconstruction.`,
    specialties: ['Dental Implants', 'Full-Arch Rehabilitation', 'Complex Restorative Dentistry', 'Prosthodontics', 'Periodontics', 'Endodontics'],
    accolades: ['Consultant Restorative Dentist', 'Clinical Director', '25+ Years Experience'],
  },
  {
    id: 'dr-senanayake',
    name: 'Dr Gihani Senanayake',
    role: 'General Dentist',
    credentials: 'BDS | GDC: 123456',
    image: ASSETS.drGihani,
    highlight: 'Gentle Care',
    shortBio: 'Dr Gihani Senanayake is a King\'s College London graduate with broad hospital and practice experience, known for her gentle approach and high standards of patient-centred care.',
    fullBio: `Dr Gihani Senanayake is a graduate of King's College London and has built broad clinical experience across both hospital and general practice settings.

She has worked in Paediatric Dentistry at Newcastle Dental Hospital, as well as in Restorative Dentistry at Queen Alexandra Hospital under the supervision of Consultant Mr Romeed, developing strong skills in the management of diverse and complex dental needs.

Dr Gihani is gentle, detail-focused, and committed to high standards of patient-centred care. She enjoys building long-standing relationships with her patients and gives particular support to those who feel nervous or anxious about treatment.

Outside dentistry, she enjoys playing the violin and travelling.`,
    specialties: ['General Dentistry', 'Restorative Dentistry', 'Complex Case Support', 'Nervous Patient Care'],
    accolades: ['King\'s College London', 'Hospital Experience', 'Patient-Centred Care'],
  },
  {
    id: 'dr-bala',
    name: 'Dr Jay Bala',
    role: 'Dentist',
    credentials: 'BDS | GDC: 100357',
    image: ASSETS.drJay,
    highlight: 'Aesthetic Expertise',
    shortBio: 'Dr Jay Bala qualified in 2001 and has over two decades of experience across general, cosmetic, and implant dentistry, combining surgical precision with a gentle and reassuring manner.',
    fullBio: `Dr Jay Bala qualified as a dentist in India in 2001. She later moved to the United Kingdom, completed the UK qualifying examinations, and finished vocational training in 2005.

Committed to maintaining high standards and staying current with advances in dentistry, she completed the Membership of the Joint Dental Faculties of the Royal College of Surgeons of England (MJDF RCS Eng) in 2008 and has trained further in conscious sedation, facial aesthetics, and dental implants.

To deepen her oral surgery experience, she worked as a Staff Grade in Oral and Maxillofacial Surgery at Royal Sussex County Hospital in Brighton in 2009. She completed a Master's degree in Dental Implantology in 2015 and, most recently, a year-long Certificate in Cosmetic Dentistry and Aesthetic Restorative Dentistry with Dr Chris Orr in 2024-25.

Dr Bala enjoys all aspects of general dentistry. She joined Grand Avenue Dental Practice in Worthing in 2017 and, since relocating to London, continues to work in Worthing two days per week while also practising in London clinics with a focus on cosmetic dentistry and dental implants.

Known for her gentle manner and attention to detail, she combines surgical skill with aesthetic expertise to help patients achieve natural, confident smiles in a comfortable environment.

Outside dentistry, she enjoys time with her family and three young children, as well as badminton, running, cycling, yoga, travel, and DIY projects.`,
    specialties: ['General Dentistry', 'Cosmetic Dentistry', 'Dental Implants', 'Aesthetic Restorations'],
    accolades: ['MJDF RCS Eng', 'MSc Implantology', 'Cosmetic Dentistry Certification'],
  },
];

function ClinicianCard({ clinician, index, isInitiallyExpanded = false }) {
  const [isExpanded, setIsExpanded] = useState(isInitiallyExpanded);
  const isEven = index % 2 === 0;

  useEffect(() => {
    setIsExpanded(isInitiallyExpanded);
  }, [isInitiallyExpanded]);

  return (
    <motion.div
      id={clinician.id}
      data-clinician-id={clinician.id}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="relative"
    >
      <div className={`grid lg:grid-cols-2 gap-0 lg:gap-12 items-center ${isEven ? '' : 'lg:flex-row-reverse'}`}>
        {/* Image Section */}
        <div className={`group relative ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
          <div className="relative aspect-[4/5] lg:aspect-[3/4] overflow-hidden rounded-3xl border border-stone-200/80 bg-white shadow-[0_18px_36px_rgba(41,37,36,0.1)] transition-all duration-500 md:group-hover:-translate-y-1 md:group-hover:shadow-[0_26px_48px_rgba(41,37,36,0.16)]">
            <img
              src={clinician.image}
              alt={clinician.name}
              className="h-full w-full object-cover transition-transform duration-700 ease-out transform-gpu md:group-hover:scale-110 md:hover:scale-110"
            />
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/45 via-transparent to-transparent transition-opacity duration-500 md:group-hover:opacity-90" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-transparent opacity-0 transition-opacity duration-500 md:group-hover:opacity-100" />
            
            {/* Highlight badge */}
            <div className="absolute top-6 left-6 transition-transform duration-500 md:group-hover:-translate-y-0.5">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/95 backdrop-blur-sm rounded-full">
                <span className="w-2 h-2 bg-accent-gold rounded-full" />
                <span className="font-body text-sm font-medium text-charcoal">{clinician.highlight}</span>
              </span>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className={`py-8 lg:py-0 ${isEven ? 'lg:order-2' : 'lg:order-1'}`}>
          {/* Credentials badge */}
          {clinician.credentials && (
            <p className="font-accent text-xs tracking-widest text-accent-gold mb-3">{clinician.credentials}</p>
          )}
          
          <h2 className="font-heading text-3xl md:text-4xl text-charcoal mb-2">{clinician.name}</h2>
          <p className="font-body text-lg text-stone-500 mb-6">{clinician.role}</p>

          {/* Accolades */}
          <div className="flex flex-wrap gap-3 mb-6">
            {clinician.accolades.map((accolade, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-stone-100 rounded-full"
              >
                <Award size={14} className="text-accent-gold" strokeWidth={1.5} />
                <span className="font-body text-xs text-charcoal">{accolade}</span>
              </span>
            ))}
          </div>

          <p className="text-body mb-6">{clinician.shortBio}</p>

          {/* Specialties */}
          <div className="mb-6">
            <p className="font-body text-sm font-medium text-charcoal mb-3">Specialises in:</p>
            <div className="flex flex-wrap gap-2">
              {clinician.specialties.map((specialty, i) => (
                <span
                  key={i}
                  className="px-3 py-1 border border-stone-200 rounded-full font-body text-xs text-stone-600"
                >
                  {specialty}
                </span>
              ))}
            </div>
          </div>

          {/* Expand/Collapse */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            data-testid={`expand-${clinician.id}`}
            className="inline-flex items-center gap-2 font-body font-medium text-charcoal hover:text-accent-gold transition-colors group"
          >
            <span className="relative">
              {isExpanded ? 'Read Less' : 'Read Full Bio'}
              <span className="absolute bottom-0 left-0 w-0 h-px bg-accent-gold transition-all duration-300 group-hover:w-full" />
            </span>
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          {/* Expanded Bio */}
          <motion.div
            initial={false}
            animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="mt-6 pt-6 border-t border-stone-200">
              <div className="font-body text-sm text-stone-600 space-y-4 whitespace-pre-line">
                {clinician.fullBio.split('\n\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Team() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedClinicianId = searchParams.get('clinician');

  const scrollToClinician = useCallback((clinicianId, behavior = 'smooth') => {
    if (!clinicianId) return;
    const headerOffset = 96;
    let attempts = 0;
    const maxAttempts = 12;

    const tryScroll = () => {
      const target = document.getElementById(clinicianId);
      if (target) {
        const targetTop = target.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: Math.max(targetTop, 0), behavior });
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        window.requestAnimationFrame(tryScroll);
      }
    };

    window.requestAnimationFrame(tryScroll);
  }, []);

  const handleClinicianTileClick = useCallback((event, clinicianId) => {
    if (
      event.metaKey ||
      event.altKey ||
      event.ctrlKey ||
      event.shiftKey
    ) {
      return;
    }

    event.preventDefault();
    const currentClinicianId = searchParams.get('clinician');
    if (currentClinicianId !== clinicianId) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('clinician', clinicianId);
      setSearchParams(nextParams);
    }

    // Scroll immediately, then once more after expansion/layout settles.
    scrollToClinician(clinicianId, 'smooth');
    window.setTimeout(() => {
      scrollToClinician(clinicianId, 'smooth');
    }, 180);
  }, [searchParams, scrollToClinician, setSearchParams]);

  useEffect(() => {
    if (!selectedClinicianId) return;
    scrollToClinician(selectedClinicianId, 'smooth');
  }, [selectedClinicianId, scrollToClinician]);

  return (
    <div className="pb-mobile-bar" data-testid="team-page">
      {/* Hero */}
      <section className="bg-gradient-to-b from-stone-100 to-warm-white py-10 md:py-14 lg:py-16" data-testid="team-hero">
        <div className="container-luxury">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-3 inline-flex items-center gap-1 rounded-full border border-stone-200/80 bg-white/85 px-3 py-1.5 shadow-[0_10px_24px_rgba(41,37,36,0.08)] backdrop-blur-sm sm:gap-2 sm:px-4 sm:py-2"
            >
              <Heart size={14} className="text-accent-gold sm:size-4" strokeWidth={1.5} />
              <span className="font-accent text-[10px] uppercase tracking-[0.16em] text-[#a57a46] sm:text-xs sm:tracking-[0.2em]">Specialist Care</span>
              <span className="h-1.5 w-1.5 rounded-full bg-accent-gold/80" />
              <span className="font-accent text-[10px] uppercase tracking-[0.16em] text-stone-500 sm:text-xs sm:tracking-[0.2em]">Personal Approach</span>
              <span className="hidden h-1.5 w-1.5 rounded-full bg-accent-gold/70 sm:inline-block" />
              <span className="hidden font-accent text-xs uppercase tracking-[0.2em] text-stone-500 sm:inline">New Malden, London</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-display mb-4"
            >
              Meet The <span className="text-accent-gold">Team</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-body-large max-w-2xl mx-auto"
            >
              We are a close, experienced team who take time to listen and explain.
              From your first visit to your final review, your care is planned around you.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
            className="mx-auto mt-8 max-w-2xl md:hidden"
          >
            <div className="grid grid-cols-2 gap-3">
              <Link
                to={`/team?clinician=${clinicians[0].id}`}
                aria-label={`Open ${clinicians[0].name} bio`}
                onClick={(event) => handleClinicianTileClick(event, clinicians[0].id)}
                className="group relative block aspect-[1/2.07] overflow-hidden rounded-2xl border border-stone-200/80 bg-white/70 shadow-[0_14px_28px_rgba(41,37,36,0.12)] transition-all duration-500 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-warm-white"
              >
                <img
                  src={clinicians[0].image}
                  alt={clinicians[0].name}
                  className="h-full w-full object-cover object-center transition-transform duration-700 ease-out transform-gpu group-active:scale-105"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-charcoal/55 via-charcoal/10 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-3 text-left">
                  <p className="font-heading text-sm text-warm-white">{clinicians[0].name}</p>
                  <p className="font-body text-xs text-stone-200">{clinicians[0].highlight}</p>
                </div>
              </Link>

              <div className="flex flex-col gap-3">
                {clinicians.slice(1).map((profile) => (
                  <Link
                    key={profile.id}
                    to={`/team?clinician=${profile.id}`}
                    aria-label={`Open ${profile.name} bio`}
                    onClick={(event) => handleClinicianTileClick(event, profile.id)}
                    className="group relative block aspect-square overflow-hidden rounded-2xl border border-stone-200/80 bg-white/70 shadow-[0_14px_28px_rgba(41,37,36,0.12)] transition-all duration-500 active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-warm-white"
                  >
                    <img
                      src={profile.image}
                      alt={profile.name}
                      className="h-full w-full object-cover object-center transition-transform duration-700 ease-out transform-gpu group-active:scale-105"
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-charcoal/55 via-charcoal/10 to-transparent" />
                    <div className="absolute inset-x-0 bottom-0 p-3 text-left">
                      <p className="font-heading text-sm text-warm-white">{profile.name}</p>
                      <p className="font-body text-xs text-stone-200">{profile.highlight}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
            className="mx-auto mt-10 hidden max-w-6xl grid-cols-3 auto-rows-[20rem] gap-4 md:grid lg:auto-rows-[22rem] xl:auto-rows-[24rem]"
          >
            {clinicians.map((profile) => (
              <Link
                key={profile.id}
                to={`/team?clinician=${profile.id}`}
                aria-label={`Open ${profile.name} bio`}
                onClick={(event) => handleClinicianTileClick(event, profile.id)}
                className="group relative block h-full overflow-hidden rounded-2xl border border-stone-200/80 bg-white/70 shadow-[0_14px_28px_rgba(41,37,36,0.12)] transition-all duration-500 md:hover:-translate-y-1 md:hover:shadow-[0_22px_42px_rgba(41,37,36,0.18)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-gold/70 focus-visible:ring-offset-2 focus-visible:ring-offset-warm-white"
              >
                <img
                  src={profile.image}
                  alt={profile.name}
                  className="h-full w-full object-cover object-center transition-transform duration-700 ease-out transform-gpu md:group-hover:scale-110 md:hover:scale-110"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-charcoal/55 via-charcoal/10 to-transparent" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/35 via-transparent to-transparent opacity-0 transition-opacity duration-500 md:group-hover:opacity-100" />
                <div className="absolute inset-x-0 bottom-0 p-3 text-left">
                  <p className="font-heading text-sm text-warm-white md:text-base">{profile.name}</p>
                  <p className="font-body text-xs text-stone-200">{profile.highlight}</p>
                </div>
              </Link>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Team Stats */}
      <section className="py-12 border-y border-stone-200 bg-white">
        <div className="container-luxury">
          <div className="grid grid-cols-3 gap-8 max-w-3xl mx-auto">
            {[
              { value: '3', label: 'Experienced Clinicians' },
              { value: '60+', label: 'Years Combined Experience' },
              { value: '1000s', label: 'Patients Treated' },
            ].map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <p className="font-heading text-3xl md:text-4xl text-charcoal mb-1">{stat.value}</p>
                <p className="font-body text-sm text-stone-500">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Clinician Cards */}
      <section className="section-spacing" data-testid="clinicians-section">
        <div className="container-luxury">
          <div className="space-y-24 md:space-y-32">
            {clinicians.map((clinician, index) => (
              <ClinicianCard
                key={clinician.id}
                clinician={clinician}
                index={index}
                isInitiallyExpanded={selectedClinicianId === clinician.id}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-20 bg-stone-50">
        <div className="container-luxury">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { icon: GraduationCap, title: 'Highly Trained', desc: 'Advanced specialist training with a strong focus on detail.' },
                { icon: Heart, title: 'Patient Focused', desc: 'We listen first and shape treatment around your priorities and comfort.' },
                { icon: Award, title: 'Trusted Standards', desc: 'Consistent, high-quality care delivered in a calm, welcoming setting.' },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center p-6"
                >
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-white shadow-sm flex items-center justify-center">
                    <item.icon size={24} className="text-accent-gold" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg text-charcoal mb-2">{item.title}</h3>
                  <p className="font-body text-sm text-stone-600">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing section-hero-echo-dark-alt" data-testid="team-cta">
        <div className="container-luxury text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="mb-6 font-heading text-3xl font-medium tracking-tight text-warm-white md:text-4xl lg:text-5xl">
              Ready To Meet The Team?
            </h2>
            <p className="mx-auto mb-8 max-w-2xl font-body text-lg leading-relaxed text-stone-200 md:text-xl">
              Book a consultation and we will talk you through your options clearly.
              No pressure, just honest advice and thoughtful care.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={BOOKING_URL}
                data-testid="team-book-btn"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-warm-white px-8 font-body font-medium text-charcoal transition-all duration-300 hover:bg-accent-gold-light"
              >
                Book Consultation
                <ArrowRight size={18} strokeWidth={1.5} />
              </a>
              <Link
                to="/referrals"
                data-testid="team-referral-btn"
                className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-white/70 bg-white/10 px-8 font-body font-medium text-warm-white transition-all duration-300 hover:bg-white hover:text-charcoal"
              >
                Refer a Specialist Case
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
